// src/pages/CRM/SFA/TerritoryManagement.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  Public as PublicIcon,
  LocationCity as CityIcon,
  Place as PlaceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { hasPermission } from '../../../../utils/permissionUtils';
import { territoryService, salesTeamService } from './sfaService';

const TerritoryManagement = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [territories, setTerritories] = useState([]);
  const [salesTeams, setSalesTeams] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  // Permissions
  const [canModify, setCanModify] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assigned_sales_team_id: '',
    is_active: true
  });

  // Region dialog
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [regionFormData, setRegionFormData] = useState({
    country: '',
    state: '',
    city: ''
  });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [territoryToDelete, setTerritoryToDelete] = useState(null);

  useEffect(() => {
    document.title = 'Territory Management | Sales Force Automation';
    checkPermissions();
    loadData();
  }, []);

  const checkPermissions = async () => {
    try {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [territoriesRes, teamsRes] = await Promise.all([
        territoryService.getAll({ include_regions: true, include_sales_team: true }),
        salesTeamService.getAll({ is_active: true })
      ]);

      if (territoriesRes.success) {
        setTerritories(territoriesRes.data);
      }
      if (teamsRes.success) {
        setSalesTeams(teamsRes.data || []);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load territories', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (territoryId) => {
    setExpandedRows(prev => ({
      ...prev,
      [territoryId]: !prev[territoryId]
    }));
  };

  const handleOpenDialog = (mode, territory = null) => {
    setDialogMode(mode);
    setSelectedTerritory(territory);
    if (mode === 'edit' && territory) {
      setFormData({
        name: territory.name || '',
        description: territory.description || '',
        assigned_sales_team_id: territory.assigned_sales_team_id || '',
        is_active: territory.is_active !== false
      });
    } else {
      setFormData({
        name: '',
        description: '',
        assigned_sales_team_id: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTerritory(null);
    setFormData({
      name: '',
      description: '',
      assigned_sales_team_id: '',
      is_active: true
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Territory name is required', { variant: 'error' });
      return;
    }

    try {
      const payload = {
        ...formData,
        assigned_sales_team_id: formData.assigned_sales_team_id || null
      };

      if (dialogMode === 'create') {
        const response = await territoryService.create(payload);
        if (response.success) {
          enqueueSnackbar('Territory created successfully', { variant: 'success' });
          loadData();
          handleCloseDialog();
        }
      } else {
        const response = await territoryService.update(selectedTerritory.id, payload);
        if (response.success) {
          enqueueSnackbar('Territory updated successfully', { variant: 'success' });
          loadData();
          handleCloseDialog();
        }
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Operation failed', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!territoryToDelete) return;

    try {
      const response = await territoryService.delete(territoryToDelete.id);
      if (response.success) {
        enqueueSnackbar('Territory deleted successfully', { variant: 'success' });
        loadData();
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to delete territory', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setTerritoryToDelete(null);
    }
  };

  const handleOpenRegionDialog = (territory) => {
    setSelectedTerritory(territory);
    setRegionFormData({ country: '', state: '', city: '' });
    setRegionDialogOpen(true);
  };

  const handleCloseRegionDialog = () => {
    setRegionDialogOpen(false);
    setSelectedTerritory(null);
    setRegionFormData({ country: '', state: '', city: '' });
  };

  const handleAddRegion = async () => {
    if (!regionFormData.country.trim()) {
      enqueueSnackbar('Country is required', { variant: 'error' });
      return;
    }

    try {
      const response = await territoryService.addRegion(selectedTerritory.id, regionFormData);
      if (response.success) {
        enqueueSnackbar('Region added successfully', { variant: 'success' });
        loadData();
        handleCloseRegionDialog();
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to add region', { variant: 'error' });
    }
  };

  const handleRemoveRegion = async (territoryId, regionId) => {
    try {
      const response = await territoryService.removeRegion(territoryId, regionId);
      if (response.success) {
        enqueueSnackbar('Region removed successfully', { variant: 'success' });
        loadData();
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to remove region', { variant: 'error' });
    }
  };

  const formatRegionDisplay = (region) => {
    const parts = [region.country];
    if (region.state) parts.push(region.state);
    if (region.city) parts.push(region.city);
    return parts.join(' > ');
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Territory Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define geographic territories for lead and opportunity assignment
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              New Territory
            </Button>
          )}
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Territories List */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>Territory Name</TableCell>
                <TableCell>Assigned Sales Team</TableCell>
                <TableCell align="center">Regions</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {territories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No territories found</Typography>
                    {canModify && (
                      <Button
                        sx={{ mt: 2 }}
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                      >
                        Create First Territory
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                territories.map((territory) => (
                  <React.Fragment key={territory.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(territory.id)}
                        >
                          {expandedRows[territory.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography fontWeight={600}>{territory.name}</Typography>
                          {territory.description && (
                            <Typography variant="body2" color="text.secondary">
                              {territory.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {territory.assigned_sales_team ? (
                          <Chip
                            label={territory.assigned_sales_team.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={territory.region_count || 0}
                          size="small"
                          color="info"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={territory.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={territory.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {canModify && (
                            <>
                              <Tooltip title="Add Region">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenRegionDialog(territory)}
                                >
                                  <PlaceIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog('edit', territory)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setTerritoryToDelete(territory);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedRows[territory.id] ? 1 : 0 }}>
                        <Collapse in={expandedRows[territory.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Regions ({territory.regions?.length || 0})
                            </Typography>
                            {territory.regions?.length > 0 ? (
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {territory.regions.map((region) => (
                                  <Chip
                                    key={region.id}
                                    label={formatRegionDisplay(region)}
                                    size="small"
                                    icon={region.city ? <CityIcon /> : region.state ? <PlaceIcon /> : <PublicIcon />}
                                    onDelete={canModify ? () => handleRemoveRegion(territory.id, region.id) : undefined}
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No regions defined. Add regions to enable auto-assignment.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Territory Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Territory' : 'Edit Territory'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Territory Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., North America, EMEA, APAC"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Team</InputLabel>
                <Select
                  value={formData.assigned_sales_team_id}
                  label="Assigned Sales Team"
                  onChange={(e) => setFormData({ ...formData, assigned_sales_team_id: e.target.value })}
                >
                  <MenuItem value="">Not Assigned</MenuItem>
                  {salesTeams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Region Dialog */}
      <Dialog open={regionDialogOpen} onClose={handleCloseRegionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Region to {selectedTerritory?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the geographic coverage. Country is required. State and City are optional for more specific matching.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={regionFormData.country}
                onChange={(e) => setRegionFormData({ ...regionFormData, country: e.target.value })}
                required
                placeholder="e.g., United States, United Kingdom"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="State/Province (Optional)"
                value={regionFormData.state}
                onChange={(e) => setRegionFormData({ ...regionFormData, state: e.target.value })}
                placeholder="e.g., California, Texas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="City (Optional)"
                value={regionFormData.city}
                onChange={(e) => setRegionFormData({ ...regionFormData, city: e.target.value })}
                placeholder="e.g., San Francisco, Austin"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegionDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAddRegion}>
            Add Region
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Territory?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{territoryToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TerritoryManagement;
